import React from 'react';
import { MapPin, Clock, Phone, Navigation, Monitor, Smartphone, Tablet, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, openingHours, products } from '../data/mock';

const StoreSection = () => {
  const productIcons = {
    Monitor,
    Smartphone,
    Tablet,
    Camera
  };

  const today = new Date().getDay();
  const daysMap = [6, 0, 1, 2, 3, 4, 5]; // Convert JS day to our array index
  const todayIndex = daysMap[today];

  return (
    <section id="boutique" className="py-24 bg-gradient-to-br from-primary-blue via-primary-blue/95 to-primary-blue relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary-cyan/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-white/10 text-white font-semibold rounded-full text-sm mb-4">
            Notre Boutique
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Venez nous rendre
            <span className="text-primary-cyan"> visite !</span>
          </h2>
          <p className="text-lg text-white/70">
            Découvrez notre espace de vente à Corbigny. Conseils personnalisés, démonstrations et service après-vente de proximité.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Store Info */}
          <div className="space-y-8">
            {/* Address Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-primary-cyan/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-7 h-7 text-primary-cyan" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Adresse</h3>
                  <p className="text-white/80 text-lg">{companyInfo.address}</p>
                </div>
              </div>

              <a
                href={companyInfo.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="w-full bg-primary-cyan hover:bg-primary-cyan/90 text-white py-6 rounded-xl text-lg font-semibold flex items-center justify-center gap-3 transition-all duration-300 hover:-translate-y-1">
                  <Navigation className="w-5 h-5" />
                  Ouvrir dans Google Maps
                </Button>
              </a>
            </div>

            {/* Opening Hours Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-primary-cyan/20 rounded-2xl flex items-center justify-center">
                  <Clock className="w-7 h-7 text-primary-cyan" />
                </div>
                <h3 className="text-xl font-bold text-white">Horaires d'ouverture</h3>
              </div>

              <div className="space-y-3">
                {openingHours.map((item, index) => (
                  <div
                    key={index}
                    className={`flex justify-between items-center py-3 px-4 rounded-xl transition-colors ${
                      index === todayIndex
                        ? 'bg-primary-cyan/20 border border-primary-cyan/30'
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <span className={`font-medium ${index === todayIndex ? 'text-primary-cyan' : 'text-white/80'}`}>
                      {item.day}
                      {index === todayIndex && <span className="ml-2 text-xs bg-primary-cyan text-white px-2 py-0.5 rounded-full">Aujourd'hui</span>}
                    </span>
                    <span className={item.isClosed ? 'text-red-400' : 'text-white'}>
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Phone CTA */}
            <a href={`tel:${companyInfo.phone.replace(/\./g, '')}`}>
              <div className="bg-gradient-to-r from-primary-cyan to-primary-cyan/80 rounded-2xl p-6 flex items-center justify-between hover:from-primary-cyan/90 hover:to-primary-cyan/70 transition-all duration-300 cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm">Une question ?</p>
                    <p className="text-white font-bold text-xl">{companyInfo.phone}</p>
                  </div>
                </div>
                <span className="text-white/60 text-2xl">→</span>
              </div>
            </a>
          </div>

          {/* Right - Products */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">Ce que nous vendons</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {products.map((product) => {
                const IconComponent = productIcons[product.icon];
                return (
                  <div
                    key={product.id}
                    className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 hover:-translate-y-1 group"
                  >
                    <div className="w-12 h-12 bg-primary-cyan/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-6 h-6 text-primary-cyan" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2">{product.title}</h4>
                    <p className="text-white/70 text-sm mb-4">{product.description}</p>
                    <div className="space-y-2">
                      {product.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className="w-1.5 h-1.5 bg-primary-cyan rounded-full" />
                          <span className="text-white/80">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Special Note */}
            <div className="mt-6 bg-yellow-500/20 border border-yellow-500/30 rounded-2xl p-5">
              <p className="text-yellow-200 text-sm">
                <strong>💡 Bon à savoir :</strong> Pour tout achat chez nous, bénéficiez d'un accompagnement personnalisé et de conseils gratuits à vie !
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoreSection;
